import cloneDeep from 'lodash-es/cloneDeep';
import get from 'lodash-es/get';
import pick from 'lodash-es/pick';
import set from 'lodash-es/set';
import orderBy from 'lodash-es/orderBy';
import {
  isObjectOfType,
  getPropertiesForDefaultObject,
  identifyDataObject,
} from './objects';
import { getCloudinaryURL, getCachedImageURLFromURL } from './images';
import variables from './variables';
import utils from './utils';
import fabric from './fabric';

const migrateImageJSON = object => {
  if (object.type !== 'image' && object.type !== 'ludusImage') {
    return object;
  }
  // Don't mutate the original object
  const o = cloneDeep(object);
  // Only work with legacy images
  if (object.type === 'image') {
    o.type = 'ludusImage';
  }
  if (!isObjectOfType(o, 'image')) {
    return o;
  }

  if (get(o, 'ludus.ludusImageVersion') && !get(o, 'ludus.url')) {
    return {};
  }

  if (parseFloat(get(o, 'ludus.ludusImageVersion')) >= 3.0) {
    const shouldMigrateURL = get(o, 'ludus.url').indexOf('a_ignore') > -1;
    if (shouldMigrateURL) {
      o.ludus.url = o.ludus.url.replace('a_ignore/', '');
      return o;
    }
    return o;
  }
  // Don't run migration twice
  if (get(o, 'ludus.ludusImageVersion') === '2.0') {
    return o;
  }

  // Take common properties from original object
  const properties = pick(o, [
    'canvasId',
    'left',
    'top',
    'flipX',
    'flipY',
    'originX',
    'originY',
    'scaleX',
    'scaleY',
    'angle',
    'opacity',
    'fill',
    'shadow',
    'globalCompositeOperation',
    'width',
    'height',
  ]);
  const additionalProperties = {};

  // URLs
  let url;
  let originalUrl = o.src;
  if (originalUrl && originalUrl.indexOf('fetch') > -1) {
    const urlIndex = originalUrl.indexOf('http', originalUrl.indexOf('fetch'));
    if (urlIndex > -1) {
      originalUrl = decodeURIComponent(originalUrl.substr(urlIndex));
      url = getCachedImageURLFromURL(originalUrl);
    }
  } else {
    url = getCloudinaryURL(originalUrl);
  }

  additionalProperties.src = o.src;
  additionalProperties.ludus = {
    originalUrl,
    url,
    ludusImageVersion: '2.0',
    originalWidth: properties.width,
    originalHeight: properties.height,
    limitedWidth: properties.width,
    limitedHeight: properties.height,
  };

  // Source
  const source = get(o, 'ludus.source');
  if (source) {
    additionalProperties.ludus.source = source;
  }

  // Hyperlink
  const hyperlink = get(o, 'ludus.hyperlink');
  if (hyperlink) {
    additionalProperties.ludus.hyperlink = hyperlink;
  }

  // Tint filter
  if (o.filters) {
    o.filters.forEach(filter => {
      if (['Tint', 'BlendColor'].includes(filter.type)) {
        additionalProperties.tint = utils.getRGBAStringFromRGBAObject({
          ...utils.parseRGBA(filter.color),
          a: filter.type === 'Tint' ? filter.opacity : filter.alpha,
        });
      }
    });
  }

  return getPropertiesForDefaultObject('image', {
    ...properties,
    ...additionalProperties,
  });
};

const migratePathGroup = object => {
  // Remove useless SVG stored in URL param of ludus (can happen in some icons8,etc)
  if (
    isObjectOfType(object, 'path-group') &&
    object.ludus.url &&
    object.ludus.url.indexOf('data:image/') >= 0
  ) {
    const o = cloneDeep(object);
    return set(o, 'ludus.url', null);
  }
  return object;
};

const migrateTextboxJSON = object => {
  if (object.type === 'textbox' || object.type === 'i-text') {
    if (object.type === 'i-text') {
      const ob = new fabric.IText(object.text, object);

      const o = cloneDeep({
        ...object,
        type: 'ludusText',
        width: ob.width * 1.05,
      });
      return o;
    }

    const o = cloneDeep(object);
    o.type = 'ludusText';
    return o;
  }
  return object;
};

const migrateShapeJSON = object => {
  // Don't mutate the original object

  if (object.type === 'path') {
    const o = cloneDeep(object);
    o.type = 'ludusPath';
    return o;
  }
  // Only work with group strokes and legacy rectangles and circles
  if (
    !isObjectOfType(object, 'groupStroke', 'rectangle', 'circle') ||
    (object.type !== 'group' &&
      object.type !== 'rect' &&
      object.type !== 'ellipse')
  ) {
    return object;
  }

  const o = cloneDeep(object);

  // Take common properties from original object
  const properties = pick(o, [
    'canvasId',
    'left',
    'top',
    'flipX',
    'flipY',
    'originX',
    'originY',
    'scaleX',
    'scaleY',
    'angle',
    'opacity',
    'fill',
    'shadow',
    'globalCompositeOperation',
  ]);
  let originalType;
  // Object is a group stroke
  if (isObjectOfType(o, 'groupStroke')) {
    // Find original type of group stroke, fallback to rectangle
    originalType =
      {
        rect: 'rectangle',
        ellipse: 'circle',
      }[get(o, 'objects.[0].type')] || 'rectangle';
    properties.innerStroke = o.objects[0].stroke;
    properties.innerStrokeWidth = o.objects[0].strokeWidth;
    properties.fill = (o.objects[1] || o.objects[0]).fill;
    if (originalType === 'rectangle') {
      // Take width, height and radii
      properties.width = o.width;
      properties.height = o.height;
      properties.rx = Math.min(
        o.objects[0].rx + o.objects[0].strokeWidth / 2,
        o.width / 2,
      );
      properties.ry = Math.min(
        o.objects[0].ry + o.objects[0].strokeWidth / 2,
        o.height / 2,
      );
    } else if (originalType === 'circle') {
      // Calculate ellipse axes from width and height
      properties.rx = o.width / 2;
      properties.ry = o.height / 2;
    }
  }
  let additionalProperties = {};
  // Object is a legacy rectangle
  if (isObjectOfType(o, 'rectangle')) {
    originalType = 'rectangle';
    // Take width, height and radii
    additionalProperties = {
      width: o.width,
      height: o.height,
      rx: o.rx,
      ry: o.ry,
    };
  }
  // Object is a legacy circle
  if (isObjectOfType(o, 'circle')) {
    originalType = 'circle';
    // Calculate ellipse axes from width and height
    additionalProperties = {
      width: o.width,
      height: o.height,
      rx: o.width / 2,
      ry: o.height / 2,
    };
  }
  // Get rid of 'transparent' color values
  properties.innerStroke =
    properties.innerStroke !== 'transparent' ? properties.innerStroke : null;
  properties.fill = properties.fill !== 'transparent' ? properties.fill : null;
  // If the object was locked, apply lock properties
  if (o.ludus && o.ludus.locked) {
    additionalProperties = {
      ...additionalProperties,
      ...variables.lockProperties,
    };
  }
  // Reconstruct Fabric-compatible JSON object with default and copied properties
  const newProperties = getPropertiesForDefaultObject(originalType, {
    ...properties,
    ...additionalProperties,
  });
  return newProperties;
};

const migrateIframeJSON = object => {
  // Don't mutate the original object

  // Only work with legacy iframes
  if (!isObjectOfType(object, 'iframe')) {
    return object;
  }
  // Don't run migration twice
  if (get(object, 'ludus.ludusIframeVersion') === '2.0') {
    return object;
  }

  const o = cloneDeep(object);

  // Reconstruct Fabric-compatible JSON object with default and copied properties
  const newProperties = getPropertiesForDefaultObject('iframe', {
    ...pick(o, [
      'canvasId',
      'objects',
      'flipX',
      'flipY',
      'scaleX',
      'scaleY',
      'width',
      'height',
      'top',
      'left',
      'shadow',
      'opacity',
      'angle',
    ]),
    ludus: {
      ...o.ludus,
      ludusIframeVersion: '2.0',
    },
  });
  return newProperties;
};

const migrateMatrixTransform = object => {
  // create missing transformMatrix used in viewer to manage group transformations
  // if ((object.ludus && object.ludus.transformMatrix) || object.type === 'group') {
  //   return object;
  // }

  if (get(object, 'ludus.transformMatrix')) {
    return object;
  }

  const _o = cloneDeep(object);

  let _object = {};
  switch (_o.type) {
    case 'ludusCircle':
      _object = new fabric.LudusCircle(_o);
      break;
    case 'ludusRect':
      _object = new fabric.LudusRect(_o);
      break;
    default:
      _object = new fabric.Object(_o);
  }

  _object.scaleX = object.scaleX || 1;
  _object.scaleY = object.scaleY || 1;
  _object.top = object.top || 0;
  _object.left = object.left || 0;
  // old lines were using a wrong default originXY (center), the real default in fabric is top/left
  // we have to correct this but forcing a default origin "center" for these lines
  _object.originX =
    object.originX || (_object.type === 'ludusLine' && 'center');
  _object.originY =
    object.originY || (_object.type === 'ludusLine' && 'center');
  const matrix = _object.calcTransformMatrix(true);

  set(_o, 'ludus.transformMatrix', matrix);

  return _o;
};

const migrateJSONObject = object => {
  let json = object;
  json = migratePathGroup(json);
  json = migrateImageJSON(json);
  json = migrateShapeJSON(json);
  json = migrateIframeJSON(json);
  json = migrateTextboxJSON(json);
  json = migrateMatrixTransform(json);

  if (get(json, 'ludus.order') === undefined) {
    json = cloneDeep(json);
    set(json, 'ludus.order', object.ludus?.order || 0);
  }

  // avoid wrongly locked objects: only objects with a ref or ludus.locked can be locked
  if (
    json.ludus &&
    !json.ludus.referenceId &&
    !json.ludus.locked &&
    (!json.evented || !json.selectable || !json.editable) &&
    json.type !== 'ludusGuideline'
  ) {
    json = cloneDeep(json);
    set(json, 'evented', true);
    set(json, 'selectable', true);
    set(json, 'editabled', true);
  }

  identifyDataObject(json, false);
  return json;
};

export const migrateSlideContentObjects = objects =>
  orderBy(
    Array.isArray(objects)
      ? objects
      : Object.values(objects || {}).map(object => migrateJSON(object)),
    'ludus.order',
  );

export const migrateSlideContent = content => {
  return migrateJSON({
    ...content,
    objects: migrateSlideContentObjects(content.objects),
  });
};

// Called in Container, editor index & symbol operations
export const migrateJSON = json => {
  if (!json) {
    // console.trace('MIGRATE JSON', json);
    return;
  }
  if (
    json.objects &&
    !isObjectOfType(
      json,
      'groupStroke',
      'path-group',
      'iframe',
      'image',
      'title',
      'ludusLine',
    )
  ) {
    // If json object is a group but not a group stroke,
    // iterate and migrate all its objects

    return {
      ...json,
      objects: orderBy(
        Array.isArray(json.objects)
          ? json.objects.map(o => migrateJSON(o))
          : Object.values(json.objects).map(o => migrateJSON(o)),
        'ludus.order',
      ),
    };
  }
  // Migrate object
  return migrateJSONObject(json);
};
